import { ChangeDetectionStrategy, Component, OnInit, inject, input, output } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { OUTCOME_KINDS, OutcomeKind } from '../../../core/models/interaction.model';
import { ReportFilters } from '../../../core/models/report.model';
import { ClockSyncService } from '../../../core/services/clock-sync.service';
import { TagService } from '../../../core/services/tag.service';
import { TeamService } from '../../../core/services/team.service';
import { TranslationService } from '../../../core/services/translation.service';
import { UserService } from '../../../core/services/user.service';

type DateRangeKind = 'today' | 'week' | 'month' | 'custom';

interface ReportFilterModel {
  range: DateRangeKind;
  from: string;
  to: string;
  userId: string;
  teamId: string;
  tagId: string;
  outcomeKind: string;
}

/**
 * The one filter UI every report page embeds (design doc §8: "Common filters on every
 * report: date range · user · team · tags · outcome"). These narrow the rows a viewer is
 * already permitted to see -- when `includeAllUsers` is false (the viewer lacks
 * `interaction.read-others`), the user/team fields are hidden entirely rather than
 * disabled, since their own rows are already just their own.
 */
@Component({
  selector: 'app-report-filter-bar',
  standalone: true,
  imports: [ReactiveFormsModule, FormlyModule],
  templateUrl: './report-filter-bar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './report-filter-bar.component.css',
})
export class ReportFilterBarComponent implements OnInit {
  private userService = inject(UserService);
  private teamService = inject(TeamService);
  private tagService = inject(TagService);
  private clockSync = inject(ClockSyncService);
  private translate = inject(TranslationService);

  includeAllUsers = input(false);
  filtersChange = output<ReportFilters>();

  form = new FormGroup({});
  model: ReportFilterModel = { range: 'today', from: '', to: '', userId: '', teamId: '', tagId: '', outcomeKind: '' };
  fields: FormlyFieldConfig[] = [];

  async ngOnInit(): Promise<void> {
    const [users, teams, tags] = await Promise.all([
      this.userService.getAll(false),
      this.teamService.getAll(),
      this.tagService.getActive(),
    ]);

    const t = (key: string) => this.translate.translate(key);
    this.fields = [
      {
        key: 'range',
        type: 'native-select',
        props: {
          label: t('reports.filters.range'),
          showLabel: true,
          options: [
            { value: 'today', label: t('reports.filters.today') },
            { value: 'week', label: t('reports.filters.thisWeek') },
            { value: 'month', label: t('reports.filters.thisMonth') },
            { value: 'custom', label: t('reports.filters.custom') },
          ],
        },
      },
      {
        key: 'from',
        type: 'datetime',
        props: { label: t('reports.filters.from'), attributes: { 'data-testid': 'report-filter-from' } },
        expressions: { hide: (field) => field.model?.range !== 'custom' },
      },
      {
        key: 'to',
        type: 'datetime',
        props: { label: t('reports.filters.to'), attributes: { 'data-testid': 'report-filter-to' } },
        expressions: { hide: (field) => field.model?.range !== 'custom' },
      },
      {
        key: 'userId',
        type: 'native-select',
        props: {
          label: t('reports.filters.user'),
          showLabel: true,
          options: [{ value: '', label: t('reports.filters.everyone') }, ...users.map((u) => ({ value: u.id, label: u.full_name || u.email }))],
        },
        expressions: { hide: () => !this.includeAllUsers() },
      },
      {
        key: 'teamId',
        type: 'native-select',
        props: {
          label: t('reports.filters.team'),
          showLabel: true,
          options: [{ value: '', label: t('reports.filters.allTeams') }, ...teams.map((team) => ({ value: team.id, label: team.name }))],
        },
        expressions: { hide: () => !this.includeAllUsers() },
      },
      {
        key: 'tagId',
        type: 'native-select',
        props: {
          label: t('reports.filters.tag'),
          showLabel: true,
          options: [{ value: '', label: t('reports.filters.allTags') }, ...tags.map((tag) => ({ value: tag.id, label: tag.label }))],
        },
      },
      {
        key: 'outcomeKind',
        type: 'native-select',
        props: {
          label: t('reports.filters.outcome'),
          showLabel: true,
          options: [{ value: '', label: t('reports.filters.allOutcomes') }, ...OUTCOME_KINDS.map((o) => ({ value: o, label: t(`outcomes.${o}`) }))],
        },
      },
    ];

    this.form.valueChanges.subscribe(() => this.emitFilters());
    this.emitFilters();
  }

  private emitFilters(): void {
    const { from, to } = this.dateRangeBounds();
    this.filtersChange.emit({
      from,
      to,
      userId: this.model.userId || undefined,
      teamId: this.model.teamId || undefined,
      tagId: this.model.tagId || undefined,
      outcomeKind: (this.model.outcomeKind as OutcomeKind) || undefined,
    });
  }

  private dateRangeBounds(): { from: number; to: number } {
    const now = this.clockSync.now();
    if (this.model.range === 'custom') {
      const from = this.model.from ? new Date(this.model.from).getTime() : 0;
      const to = this.model.to ? new Date(this.model.to).getTime() : now;
      return { from, to };
    }
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    if (this.model.range === 'week') start.setDate(start.getDate() - start.getDay());
    if (this.model.range === 'month') start.setDate(1);
    return { from: start.getTime(), to: now };
  }
}
